import React, { useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { type Row, format, text } from './inventoryValues';

/** Month/page navigation only changes the viewport; the detail export retains every source row. */
export default function RoomAvailabilityMatrix({
  rows,
  onSelect,
  chosenMonth,
  onMonthChange,
}: {
  rows: Row[];
  onSelect: (row: Row) => void;
  chosenMonth: string;
  onMonthChange: (month: string) => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [page, setPage] = useState(0);
  const months = [...new Set(rows.map((r) => text(r.availability_day_of_week).slice(0, 7)))].filter(Boolean).sort();
  const month = months.includes(chosenMonth) ? chosenMonth : months[0];
  const monthRows = rows.filter((r) => text(r.availability_day_of_week).startsWith(month ?? '__none__'));
  const groups = new Map<string, Row[]>();
  monthRows.forEach((r) => {
    const key = JSON.stringify([r.room_type_name]);
    groups.set(key, [...(groups.get(key) ?? []), r]);
  });
  const rooms = [...groups.values()].sort((a, b) => text(a[0].room_type_name).localeCompare(text(b[0].room_type_name)));
  const safePage = Math.min(page, Math.max(0, Math.ceil(rooms.length / 25) - 1));
  const days = month ? new Date(Number(month.slice(0, 4)), Number(month.slice(5)), 0).getDate() : 0;
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ bgcolor: '#0f464f', color: '#fff', px: 0.75, py: 0.625 }}
      >
        <Box>
          <Typography component="h2" variant="h6" sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>
            Check-in availability by room type
          </Typography>
          <Typography variant="caption">
            Check-In is categorical; each mark shows its orphan room count. Select a date to inspect its room details.
          </Typography>
        </Box>
        {month && (
          <TextField
            select
            size="small"
            label="Availability month"
            value={month}
            onChange={(e) => {
              onMonthChange(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 140, bgcolor: tokens.surface, '& .MuiInputLabel-root': { bgcolor: tokens.surface } }}
          >
            {months.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {rooms.length === 0 ? (
        <Typography sx={{ px: 2, pb: 2 }}>No rows match these filters.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Room check-in availability matrix">
            <TableHead
              sx={{
                '& .MuiTableCell-root': { bgcolor: tokens.accent, color: theme.palette.getContrastText(tokens.accent) },
              }}
            >
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Room type</TableCell>
                {Array.from({ length: days }, (_, i) => (
                  <TableCell key={i} align="center">
                    {i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.slice(safePage * 25, safePage * 25 + 25).map((rs) => {
                const dates = new Map<string, Row[]>();
                rs.forEach((row) => {
                  const date = text(row.availability_day_of_week).slice(0, 10);
                  dates.set(date, [...(dates.get(date) ?? []), row]);
                });
                return (
                  <TableRow key={text(rs[0].room_type_name)}>
                    <TableCell>
                      <Typography variant="body2">{text(rs[0].room_type_name)}</Typography>
                    </TableCell>
                    {Array.from({ length: days }, (_, i) => {
                      const date = `${month}-${String(i + 1).padStart(2, '0')}`,
                        marks = dates.get(date) ?? [];
                      return (
                        <TableCell key={date} align="center" sx={{ p: 0.5, minWidth: 54 }}>
                          {marks.length ? (
                            <Stack spacing={0.5}>
                              {marks.map((row) => {
                                const available = row.checkin_available === 'Available';
                                const color = available
                                  ? theme.palette.success.light
                                  : row.checkin_available === 'Not available'
                                    ? theme.palette.warning.light
                                    : theme.palette.action.hover;
                                return (
                                  <Tooltip
                                    key={text(row.checkin_available)}
                                    title={`Check-In ${text(row.checkin_available)} · Vendor ${text(row.vendor_name)} · Blackout ${text(row.blackout)} · Minimum duration ${text(row.min_duration)} · Orphan rooms ${format(row.orphan_rooms)}`}
                                  >
                                    <Button
                                      variant="text"
                                      onClick={() => onSelect(row)}
                                      aria-label={`${text(row.room_type_name)}, ${date}: ${text(row.checkin_available)}, ${format(row.orphan_rooms)} orphan rooms`}
                                      sx={{
                                        border: 0,
                                        borderRadius: 0.5,
                                        p: 0.75,
                                        width: '100%',
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        fontVariantNumeric: 'tabular-nums',
                                        textTransform: 'none',
                                        display: 'block',
                                        bgcolor: color,
                                        color: theme.palette.getContrastText(color),
                                      }}
                                    >
                                      <Typography variant="caption" component="span" display="block">
                                        {text(row.checkin_available)}
                                      </Typography>
                                      {format(row.orphan_rooms)}
                                    </Button>
                                  </Tooltip>
                                );
                              })}
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={rooms.length}
        page={safePage}
        rowsPerPage={25}
        rowsPerPageOptions={[25]}
        onPageChange={(_, p) => setPage(p)}
      />
    </Paper>
  );
}
